import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { UpstreamApiDashboardData } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { dataErrorHandling } from 'src/components/dashboard/helper';

const fetchDashboardData = async (
  endpoint: string,
  signal?: AbortSignal,
): Promise<UpstreamApiDashboardData> =>
  HttpService.get(endpoint, undefined, undefined, { signal })
    .then((data) => Promise.resolve(data.data))
    .catch(dataErrorHandling);

const useSurveyRootStatisticsDataQuery = (
  dashboardId?: number,
  orgUnitId?: number,
  enabled?: boolean,
) => {
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () =>
      dashboardId && orgUnitId
        ? generatePath(apiPaths.DASHBOARD.STATISTICS_BY_ORG_UNIT, {
            dashboardId,
            orgUnitId,
          })
        : '',
    [dashboardId, orgUnitId],
  );

  return useQuery<UpstreamApiDashboardData, string>({
    queryKey: [endpoint, i18n.language],
    queryFn: ({ signal }) => fetchDashboardData(endpoint, signal),
    enabled: !!dashboardId && !!endpoint && !!enabled,
  });
};

export default useSurveyRootStatisticsDataQuery;
